// src/components/PricingGrid.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress,
  Menu,
  MenuItem
} from "@mui/material";
import * as XLSX from 'xlsx';
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon
} from "@mui/icons-material";

import api, { getPricingEntries, getBrokerEntries, savePricing, deletePricingEntry, getUsePriceMap, getPricingFrequency, getBrokerMap } from "../api";
import { useCanWritePricing, NO_PRICING_WRITE_MESSAGE } from "../permissions";
import PricingHistory from "./PricingHistory";
import { addTrackedChanges, keyEditModifiedDate, mergeUnsavedEdits, trackCellEdit } from "../utils/modifiedDates";
import {
  assignKeyEditId,
  duplicateKeysMessage,
  findDuplicateKeys,
  makeTrackingKeyOf,
  withoutClientFields,
} from "../utils/rowIdentity";
import {
  getWriteErrorMessage,
  isVersionConflict,
  withLoadedModifiedDate,
  nullToEmpty,
  ConfirmationDialog,
  DateTimeRenderer,
  useGridOperations,
  useBackgroundRefresh,
  PamGrid,
  QuickFilter,
  GridConfigMenu,
  useGridConfigs,
  normalizeBooleanValue,
  HistoryModal,
  createChoicesValidator,
  isValueNull,
  isPermissionDenied,
  getPermissionDeniedText,
  getPermissionDeniedMessage,
  RequestAccessButton,
} from "@1823-partners/core";
import config from "../data/config.json";
import { formatDate } from "../utils/dateUtils";
import { toCalendarDate } from "../utils/calendarDate";
import { parsePriceCellValue } from "../utils/priceUtils";

// Constants
const REFRESH_INTERVAL = 30000; // 30 seconds
// The PRICING writer key. The grid's composite key is ID2|BROKER only.
const PRICING_KEY_FIELDS = ['ID', 'ID2', 'BROKER', 'ASOFDT'];

const pricingCompositeKey = (row) => `${row.ID2 || ''}|${row.BROKER || ''}`;
// Unsaved edits and AG Grid row ids: TEMP_ROW_ID, else the KEY_EDIT_ID a row
// whose key was edited carries, else ID2|BROKER (see utils/rowIdentity).
const pricingTrackingKey = makeTrackingKeyOf(pricingCompositeKey);
// The key Save checks for duplicates; null while ID2 or BROKER is blank, which
// validatePricingRow reports instead.
const pricingSaveKey = (row) =>
  ['ID2', 'BROKER'].every((field) => String(row[field] ?? '').trim() !== '') ? pricingCompositeKey(row) : null;
// A broker CUSIP with no pricing row yet is a placeholder, not a row: renaming
// another row onto its key fills it in, so it is no duplicate unless edited.
const isPricingRow = (row) => row._isInPricingData !== false;
// ID can be set only where no pricing row exists yet: a new row, or a broker
// placeholder. A loaded row's ID is fixed (see the ID column).
const isIdEditable = (row) => Boolean(row?.TEMP_ROW_ID) || row?._isInPricingData === false;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// A calendar date as a whole UTC day number (see toCalendarDate), so the local
// timezone cannot shift it. null when it is not a real date.
const calendarDayNumber = (value) => {
  const date = toCalendarDate(value);
  if (!date) return null;
  const [year, month, day] = date.split('-').map(Number);
  return Date.UTC(year, month - 1, day) / MS_PER_DAY;
};

const PricingGrid = ({ userId, selectedDate, onDataChange }) => {
  const canWritePricing = useCanWritePricing();
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [selectedEntryForHistory, setSelectedEntryForHistory] = useState(null);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [newRowCount, setNewRowCount] = useState(0);
  const [quickFilterText, setQuickFilterText] = useState('');
  const [rowData, setRowData] = useState([]);
  // The rows of the latest render, for handlers that read the grid after an
  // await (the upload merge) rather than the rows their closure captured.
  const rowDataRef = useRef(rowData);
  rowDataRef.current = rowData;
  const [error, setError] = useState(null);
  // Read failures are separate from action failures so a recovered background
  // refresh can clear only its own banner without erasing a denied write.
  const [loadError, setLoadError] = useState(null);
  // The denial *object* alongside the message: RequestAccessButton reads
  // suggested_roles off it, which the message string cannot carry. Cleared
  // wherever `error` is cleared or replaced, or a later unrelated failure would
  // still show a button naming a role from an earlier denial.
  const [deniedError, setDeniedError] = useState(null);
  // A failed *background* refresh, tracked apart from `error`. The 30-second tick
  // must not overwrite a write failure the user still needs to act on, but it also
  // must not hide that the grid has stopped updating: if Beacon goes away, these
  // rows silently go stale. Separate state lets both facts show at once, which
  // conditioning the shared banner on deniedError could not — a generic save
  // failure is user-facing too and would still have been clobbered.
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [updatedRows, setUpdatedRows] = useState({}); // Dictionary to track rows with updated cells
  const [showOnlyHighlighted, setShowOnlyHighlighted] = useState(false); // Filter to show only invalid broker rows
  const [usePriceMap, setUsePriceMap] = useState({}); // Map of broker -> {id: use_price, id: use_price...}
  const [brokerMap, setBrokerMap] = useState({}); // Map of broker -> source information
  const [displayedRowCount, setDisplayedRowCount] = useState(0); // Number of rows currently displayed
  const [uploadMenuAnchor, setUploadMenuAnchor] = useState(null); // Upload dropdown menu anchor
  const [uploadType, setUploadType] = useState(null); // Track which upload type was selected

  const theme = useTheme();
  const fileInputRef = useRef(null);
  // Every replacement of the action banner advances this token. A delayed clear
  // may only remove the exact error generation that scheduled it, so an older
  // validation/upload timer cannot erase a later permission denial.
  const actionErrorGenerationRef = useRef(0);
  // loadEntries request bookkeeping (see loadEntries).
  const loadGenerationRef = useRef(0);
  const latestVisibleLoadRef = useRef(0);
  const loadsInFlightRef = useRef(0);
  // Rows under an edited key: tracking key -> the key and MODIFIED_DATE they
  // had before, so editing the key back sends that MODIFIED_DATE again
  // (keyEditModifiedDate). Cleared with updatedRows.
  const loadedKeysRef = useRef({});
  // The broker rows the current rowData was merged with, and the date that
  // rowData was loaded for; both are reported to onDataChange with the rows.
  const [brokersData, setBrokersData] = useState([]);
  const [loadedDate, setLoadedDate] = useState(null);

  // A response that lands after unmount must not touch state.
  useEffect(() => () => {
    loadGenerationRef.current += 1;
  }, []);

  const clearActionError = useCallback(() => {
    actionErrorGenerationRef.current += 1;
    setError(null);
    setDeniedError(null);
  }, []);

  const showActionError = useCallback((message, denial = null) => {
    actionErrorGenerationRef.current += 1;
    setError(message);
    setDeniedError(denial);
  }, []);

  const showTemporaryActionError = useCallback((message, timeoutMs) => {
    const generation = actionErrorGenerationRef.current + 1;
    actionErrorGenerationRef.current = generation;
    setError(message);
    setDeniedError(null);
    setTimeout(() => {
      if (actionErrorGenerationRef.current !== generation) return;
      actionErrorGenerationRef.current += 1;
      setError(null);
      setDeniedError(null);
    }, timeoutMs);
  }, []);

  // Use 1823-core grid operations hook for grid utilities
  const { gridRef } = useGridOperations();

  // Per-user grid layout persistence. Own the hook here (rather than letting
  // GridConfigMenu instantiate it) so we can bridge gridConfigs.onGridReady to
  // PamGrid — without that bridge a slow grid mount can miss the default-layout
  // auto-apply. Reuses the same gridRef passed to <PamGrid ref=…/>.
  const gridConfigs = useGridConfigs(api, {
    app: 'pricing',
    gridId: 'pricing',
    gridApiRef: gridRef,
  });


  // Helper function to create composite key
  const getCompositeKey = useCallback((broker, id) => {
    return `${id || ''}|${broker || ''}`;
  }, []);

  // AG Grid row id: the key edit tracking uses (pricingTrackingKey), so a
  // highlighted edit and its grid node agree, and two rows sharing ID2|BROKER
  // stay two grid rows. PamGrid may hand over the row data itself or AG Grid's
  // { data } params.
  const getPricingRowId = useCallback((params) => pricingTrackingKey(params?.data ?? params ?? {}), []);

  // Helper function to get current date in YYYY-MM-DD format (timezone agnostic)
  const getCurrentDateString = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, '0');
    const day = now.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);


  // Helper function to create broker-CUSIP combinations from brokers data
  const createBrokerCusipCombinations = useCallback((brokersData) => {
    const combinations = [];

    brokersData.forEach(broker => {
      const cusips = broker.CUSIPS || '';
      if (cusips.trim()) {
        // Split CUSIPS by comma and trim whitespace
        const cusipList = cusips.split(',').map(cusip => cusip.trim()).filter(cusip => cusip);

        // Remove duplicates within this broker's CUSIP list
        const uniqueCusips = [...new Set(cusipList)];

        uniqueCusips.forEach(cusip => {
          combinations.push({
            broker: broker.BROKER,
            cusip: cusip,
            key: `${broker.BROKER}|${cusip}`
          });
        });
      }
    });

    return combinations;
  }, []);

  // Helper function to merge pricing data with broker-CUSIP combinations
  const mergePricingWithBrokerData = useCallback((pricingData, brokerCombinations) => {
    const mergedData = [];
    // A loaded row's identity is ID2|BROKER (getCompositeKey, and the tracking
    // key / getRowId until its key is edited).
    // A broker CUSIP is covered by a pricing row whose ID2 *or* ID names it, so
    // a row with ID !== ID2 never gets a second, synthetic quote under the
    // same ID2|BROKER key.
    const coveredKeys = new Set();

    // First, add all existing pricing data
    pricingData.forEach(pricingEntry => {
      coveredKeys.add(getCompositeKey(pricingEntry.BROKER, pricingEntry.ID2));
      if (pricingEntry.ID) {
        coveredKeys.add(getCompositeKey(pricingEntry.BROKER, pricingEntry.ID));
      }

      // Determine if this pricing entry exists in broker combinations
      const existsInBroker = brokerCombinations.some(combo =>
        combo.broker === pricingEntry.BROKER &&
        (combo.cusip === pricingEntry.ID2 || combo.cusip === pricingEntry.ID)
      );

      mergedData.push({
        ...pricingEntry,
        _isInBrokerData: existsInBroker,
        _isInPricingData: true
      });
    });

    // Then, add missing broker-CUSIP combinations as new rows
    brokerCombinations.forEach(combo => {
      const key = getCompositeKey(combo.broker, combo.cusip);

      if (!coveredKeys.has(key)) {
        // One broker can have several broker rows (one per PRICE_FILE) listing
        // the same CUSIP; add its synthetic row once.
        coveredKeys.add(key);
        // Create a new row for this broker-CUSIP combination
        const newRow = {
          BROKER: combo.broker,
          ID2: combo.cusip,
          ID: combo.cusip, // Duplicate CUSIP as ID as requested
          PRICE: 0, // Default price to 0
          USE_PRICE: '', // Default Use Price to empty string
          DESCRIPTION: '',
          EMAIL: '',
          NOTES: '',
          ASOFDT: selectedDate ? formatDate(selectedDate) : '',
          RECEIVED_DATE: '',
          // No pricing row yet: a null MODIFIED_DATE token on save.
          MODIFIED_DATE: null,
          MODIFIED_BY: null,
          _isInBrokerData: true,
          _isInPricingData: false,
          _isNewRow: true
        };

        mergedData.push(newRow);
      }
    });

    return mergedData;
  }, [getCompositeKey, selectedDate]);


  // adoptServerModifiedDates: after a 409 the preserved edits move onto the
  // reloaded MODIFIED_DATEs, so saving again is the user's deliberate re-apply.
  // Otherwise edited rows keep the MODIFIED_DATE their edits were based on.
  //
  // Only the latest load may touch state: each call takes a generation, and a
  // response whose generation is no longer current (the date changed, or a
  // later reload started) is dropped, so a slow earlier request can never put
  // another date's rows under this date. The 30-second background tick
  // (`background`) is skipped while any other load is in flight, so it cannot
  // supersede a date switch or a post-save / post-409 reload.
  const loadEntries = useCallback(async (preserveLocalChanges = false, silent = false, { adoptServerModifiedDates = false, background = false } = {}) => {
    if (!selectedDate) {
      return;
    }
    if (background && loadsInFlightRef.current > 0) {
      return;
    }

    const generation = ++loadGenerationRef.current;
    if (!silent) {
      latestVisibleLoadRef.current = generation;
    }
    loadsInFlightRef.current += 1;
    const requestDate = selectedDate;
    const isCurrent = () => generation === loadGenerationRef.current;

    try {
      if (!silent) {
        setIsLoading(true);
      }
      // A silent load is the 30-second background refresh: it shows no spinner,
      // and it must not touch the banner either. Clearing would take a write
      // denial's Request access button away seconds after the user saw it.
      if (!silent) {
        setLoadError(null);
        clearActionError();
      }

      // Load both pricing and broker data
      const [pricingResponse, brokersResponse] = await Promise.all([
        getPricingEntries(selectedDate),
        getBrokerEntries()
      ]);
      if (!isCurrent()) return;

      // Format pricing data
      const formattedPricingData = pricingResponse.data.map(entry => {
        // Convert all null values to empty strings
        const formattedEntry = nullToEmpty(entry);
        // Add specific formatting for date fields (MODIFIED_DATE kept as-is for processing, formatted on display)
        formattedEntry.ASOFDT = formatDate(formattedEntry.ASOFDT);
        formattedEntry.RECEIVED_DATE = formatDate(formattedEntry.RECEIVED_DATE);
        // Format boolean field
        formattedEntry.USE_PRICE = formattedEntry.USE_PRICE === true ? 'Y' : formattedEntry.USE_PRICE === false ? 'N' : '';
        return formattedEntry;
      });

      // Format broker data
      // Convert all null values to empty strings
      const formattedBrokersData = brokersResponse.data.map(nullToEmpty);

      // Create broker-CUSIP combinations
      const brokerCombinations = createBrokerCusipCombinations(formattedBrokersData);

      // Merge pricing data with broker combinations
      const mergedData = mergePricingWithBrokerData(formattedPricingData, brokerCombinations);

      if (preserveLocalChanges) {
        // Use blotter-style preservation logic - simpler and more robust
        setUpdatedRows(currentUpdatedRows => {
          setRowData(currentRowData => {
            // If preserving local changes, merge them back into the fresh data
            let finalData = mergedData;

            if (Object.keys(currentUpdatedRows).length > 0 || currentRowData.some(row => row.TEMP_ROW_ID)) {
              // First, apply unsaved changes to existing server data. A row whose
              // key was edited is kept whole, since only its edited fields are tracked.
              const updatedServerData = mergeUnsavedEdits({
                freshRows: mergedData,
                currentRows: currentRowData,
                changesByKey: currentUpdatedRows,
                trackingKeyOf: pricingTrackingKey,
                keyFields: PRICING_KEY_FIELDS,
                adoptServerModifiedDates,
              });

              // Collect all new rows that need to be preserved (preserve as-is from currentRowData)
              const preservedNewRows = [];
              currentRowData.forEach(existingRow => {
                if (existingRow.TEMP_ROW_ID) {
                  // New rows should always be preserved since they don't exist in server data
                  preservedNewRows.push(existingRow); // Preserve exactly as-is
                }
              });

              // Put new rows at the top, followed by server data
              finalData = [...preservedNewRows, ...updatedServerData];
            }

            return finalData;
          });

          // Return the preserved tracking state
          return currentUpdatedRows;
        });
      } else {
        setRowData(mergedData);
        setUpdatedRows({});
        loadedKeysRef.current = {};
      }
      setBrokersData(formattedBrokersData);
      setLoadedDate(requestDate);
      // Clear read-only banners only after the response has been processed into
      // fresh rows; receiving an unusable response is not a successful refresh.
      setLoadError(null);
      setRefreshFailed(false);
    } catch (error) {
      if (!isCurrent()) return;
      console.error("Error fetching entries:", error);
      // Same rule on the way out: a background refresh must not replace what the
      // user is reading. Setting the message here while a write's denial object
      // stayed would also pair a load error with a Request access button that has
      // nothing to do with it.
      if (silent) {
        // Say the data is stale without disturbing whatever the banner holds.
        setRefreshFailed(true);
      } else if (isPermissionDenied(error)) {
        setLoadError(getPermissionDeniedMessage(error));
      } else {
        setLoadError("Failed to load pricing entries. Please try again.");
      }
    } finally {
      loadsInFlightRef.current -= 1;
      // Only the newest visible load ends the spinner; an older one that was
      // superseded by another visible load leaves it to that load.
      if (!silent && generation === latestVisibleLoadRef.current) {
        setIsLoading(false);
      }
    }
  }, [selectedDate, createBrokerCusipCombinations, mergePricingWithBrokerData, clearActionError]);


  // Load use price map when selectedDate changes
  // (a response for a date the user has since left is dropped).
  useEffect(() => {
    let cancelled = false;
    const loadUsePriceMap = async () => {
      if (!selectedDate) return;

      try {
        const response = await getUsePriceMap(selectedDate);
        if (cancelled) return;
        setUsePriceMap(response.data || {});
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading use price map:', error);
        // Set empty map as fallback
        setUsePriceMap({});
      }
    };

    loadUsePriceMap();
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  // Load broker map when component mounts and when selectedDate changes
  // (a response for a date the user has since left is dropped).
  useEffect(() => {
    let cancelled = false;
    const loadBrokerMap = async () => {
      try {
        const response = await getBrokerMap(selectedDate);
        if (cancelled) return;
        const brokerMapData = response.data || {};
        setBrokerMap(brokerMapData);
      } catch (error) {
        if (cancelled) return;
        console.error('Error loading broker map:', error);
        // Set empty map as fallback
        setBrokerMap({});
      }
    };

    if (selectedDate) {
      loadBrokerMap();
    }
    return () => {
      cancelled = true;
    };
  }, [selectedDate]);

  useEffect(() => {
    if (selectedDate) {
      setSuccessMessage(null); // Clear upload messages when date changes
      loadEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]); // Load entries when date changes

  // Background refresh every 30 seconds using 1823-core hook
  useBackgroundRefresh({
    // preserveLocalChanges=true, silent=true; skipped while another load runs
    fetchData: () => loadEntries(true, true, { background: true }),
    interval: REFRESH_INTERVAL,
    enabled: !!selectedDate // Only refresh when a date is selected
  });


  // Notify the parent of the rows on screen (unsaved edits included), the
  // broker rows they were merged with, and the date they belong to. Reported
  // once a load has landed, even when the date has no rows, so the aggregate
  // view never keeps showing a previous date's live rows.
  useEffect(() => {
    if (onDataChange && loadedDate) {
      onDataChange(rowData, brokersData, loadedDate);
    }
  }, [rowData, brokersData, loadedDate, onDataChange]);

  // Quick filter is now handled directly by QuickFilter component from 1823-core



  const handleViewHistory = useCallback((entryData) => {
    setSelectedEntryForHistory(entryData);
    setHistoryModalOpen(true);
  }, []);



  const handleDeleteEntry = useCallback((entryData) => {
    // Handle locally created new rows using TEMP_ROW_ID
    if (entryData.TEMP_ROW_ID) {
      setRowData(prev => prev.filter(row => row !== entryData));
      setUpdatedRows(prev => {
        const updated = { ...prev };
        delete updated[entryData.TEMP_ROW_ID];
        return updated;
      });
      return;
    }

    // Show confirmation modal for database entries
    setEntryToDelete(entryData);
    setConfirmationModalOpen(true);
  }, []);

  // Helper function to convert empty date strings to null
  const convertEmptyDatesToNull = useCallback((row) => {
    const dateFields = ['ASOFDT', 'RECEIVED_DATE', 'MODIFIED_DATE'];
    const cleanedRow = { ...row };

    dateFields.forEach(field => {
      if (cleanedRow[field] === '' || cleanedRow[field] === undefined) {
        cleanedRow[field] = null;
      }
    });

    return cleanedRow;
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!entryToDelete) return;

    try {
      clearActionError();
      setSuccessMessage(null);
      setConfirmationModalOpen(false);

      // The tombstone carries the row's loaded MODIFIED_DATE unchanged; the
      // writer stamps MODIFIED_BY and the new MODIFIED_DATE.
      await deletePricingEntry(withLoadedModifiedDate(withoutClientFields(convertEmptyDatesToNull(entryToDelete))));

      // Remove from local state on successful deletion: this row only, so
      // another row with the same key keeps its place and its edits.
      const trackingKey = pricingTrackingKey(entryToDelete);
      setRowData(prev => prev.filter(row => pricingTrackingKey(row) !== trackingKey));
      setUpdatedRows(prev => {
        const updated = { ...prev };
        delete updated[trackingKey];
        return updated;
      });
      delete loadedKeysRef.current[trackingKey];

      setSuccessMessage('Entry deleted successfully.');
      setTimeout(() => setSuccessMessage(null), 3000);

    } catch (error) {
      console.error('Error deleting pricing entry:', error);
      const writeError = getWriteErrorMessage(error, null);
      if (isPermissionDenied(error)) {
        showActionError(getPermissionDeniedText(error), error);
      } else if (writeError) {
        showActionError(writeError);
      } else {
        showTemporaryActionError('Failed to delete entry. Please try again.', 5000);
      }
      if (isVersionConflict(error)) {
        // Silent, so the conflict message stays on the banner.
        await loadEntries(true, true, { adoptServerModifiedDates: true });
      }
    } finally {
      setEntryToDelete(null);
    }
  }, [entryToDelete, convertEmptyDatesToNull, loadEntries, clearActionError, showActionError, showTemporaryActionError]);

  const cancelDelete = useCallback(() => {
    setConfirmationModalOpen(false);
    setEntryToDelete(null);
  }, []);

  const handleDuplicateEntry = useCallback((entryData) => {
    const nextRowCount = newRowCount + 1;
    setNewRowCount(nextRowCount);

    const duplicatedEntry = {
      TEMP_ROW_ID: `temp_${nextRowCount}_${Date.now()}`, // Add temporary tracking field
      ID: `new_row_${nextRowCount}`,
      ASOFDT: selectedDate || getCurrentDateString(),
      BROKER: `new_broker_${nextRowCount}`,
      DESCRIPTION: entryData.DESCRIPTION,
      EMAIL: entryData.EMAIL,
      ID2: `new_id2_${nextRowCount}`,
      PRICE: entryData.PRICE,
      USE_PRICE: entryData.USE_PRICE,
      NOTES: entryData.NOTES,
      // A new key: no loaded MODIFIED_DATE. The writer stamps both on save.
      MODIFIED_BY: null,
      MODIFIED_DATE: null,
      RECEIVED_DATE: ''
    };

    setRowData(prev => [duplicatedEntry, ...prev]);
  }, [newRowCount, selectedDate, getCurrentDateString]);

  const getCellClass = useCallback((params) => {
    const data = params.data;
    const field = params.colDef?.field;

    if (!data || !field) return '';

    // Check if this is a new row using TEMP_ROW_ID (green highlighting takes precedence)
    if (data.TEMP_ROW_ID) {
      return 'ag-cell-updated-new-row';
    }

    // For existing rows, check for changes first
    const rowChanges = updatedRows[pricingTrackingKey(data)];
    if (rowChanges && rowChanges[field]) {
      return 'ag-cell-updated';
    }

    // Check for broker data highlighting (yellow for missing from pricing, red for missing from broker except IDC and JPM Pricing Direct)
    if (data._isInBrokerData && !data._isInPricingData) {
      return 'ag-cell-missing-from-pricing';
    } else if (data._isInPricingData && !data._isInBrokerData && !["IDC", "JPM Pricing Direct"].includes(data.BROKER)) {
      return 'ag-cell-missing-from-broker';
    }

    return '';
  }, [updatedRows]);


  // Helper function to calculate NAIC Source based on broker and override source
  const calculateNAICSource = useCallback((broker) => {
    // Fallback to override source logic if no broker mapping
    const sourceAValues = ['JPM Pricing Direct', 'IDC', 'BAML', 'Broker', 'Pricing Service'];

    if (sourceAValues.includes(broker)) {
      return 'A';
    } else if (broker === 'Internal') {
      return 'D';
    } else {
      return 'C';
    }
  }, []);

  // Helper function to calculate source type (same logic as Month Over Month Pricing Checks)
  const calculateSourceType = useCallback((id2, brokerCount, broker) => {
    if (id2.includes('-U')) {
      return 'Unfunded Commit';
    } else if (brokerCount > 1) {
      return 'Multi Broker';
    } else {
      return brokerMap[broker] || '1 Broker';
    }
  }, [brokerMap]);

  const columnDefs = useMemo(() => [
    {
      headerName: "Actions",
      headerTooltip: "Available actions for this entry: view history, edit, duplicate, or delete",
      field: "actions",
      cellStyle: { textAlign: "center" },
      maxWidth: 200,
      pinned: 'left',
      cellRenderer: (params) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="View History">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleViewHistory(params.data)}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>

          <Tooltip title="Duplicate Entry">
            <IconButton
              color="secondary"
              size="small"
              onClick={() => handleDuplicateEntry(params.data)}
            >
              <DuplicateIcon />
            </IconButton>
          </Tooltip>
          {/* Same gate as Save: /delete-*-entry requires pricing.overrides:write, not a separate
              delete permission. The <span> is required, not decorative -- a disabled MUI
              IconButton has pointer-events:none, so without it the tooltip explaining WHY the
              button is dead would itself never appear on hover. */}
          <Tooltip title={!canWritePricing ? NO_PRICING_WRITE_MESSAGE : "Delete Entry"}>
            <span>
              <IconButton
                color="error"
                size="small"
                disabled={!canWritePricing}
                onClick={() => handleDeleteEntry(params.data)}
              >
                <DeleteIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      ),
      sortable: false,
      filter: false,
      getQuickFilterText: ''
    },
    // {
    //     headerName: "As Of Date",
    //     headerTooltip: "As of date for pricing",
    //     field: "ASOFDT",
    //     valueFormatter: params => formatDate(params.value),
    //     valueParser: params => formatDate(params.newValue),
    //     pinned: 'left',
    //     cellEditor: 'agDateCellEditor',
    //     cellEditorParams: {
    //         browserDatePicker: true
    //     }
    // },
    {
      headerName: "Broker",
      headerTooltip: "Broker information",
      field: "BROKER",
      pinned: 'left',
      minWidth: 150
    },
    {
      headerName: "ID",
      headerTooltip: "Identifier (editable on new rows only)",
      field: "ID",
      pinned: 'left',
      minWidth: 100,
      // ID is in the writer key but not the grid's ID2|BROKER key: changing it
      // on a saved row would insert a second row under the same ID2|BROKER
      // instead of correcting this one. Only rows with no pricing row yet (new
      // rows and broker placeholders) take an ID.
      editable: (params) => !isLoading && isIdEditable(params.data),
    },
    {
      headerName: "ID2",
      headerTooltip: "Secondary identifier",
      field: "ID2",
      pinned: 'left',
      minWidth: 100
    },
    {
      headerName: "Price",
      headerTooltip: "Security price",
      field: "PRICE",
      type: 'numericColumn',
      cellEditor: 'agNumberCellEditor',
      cellClass: getCellClass,
      valueFormatter: params => {
        if (isValueNull(params.value)) {
          return ''; // Empty - show as blank
        }
        const numValue = parseFloat(params.value);
        if (isNaN(numValue)) {
          return ''; // Invalid number - show as blank
        }
        if (numValue === 0) {
          return '0.0000'; // Zero - show as 0.0000
        }
        return numValue.toFixed(4); // Non-zero - show with 4 decimals
      },
      // A typed 0 stays 0 (agNumberCellEditor hands over a number); invalid
      // input keeps the previous price.
      valueParser: params => parsePriceCellValue(params.newValue, params.oldValue)
    },
    {
      headerName: "Use Price",
      headerTooltip: "Whether to use this price",
      field: "USE_PRICE",
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: config.booleanOptions
      }
    },
    {
      headerName: "Description",
      headerTooltip: "Security description",
      field: "DESCRIPTION",
      minWidth: 250
    },
    {
      headerName: "Received Date",
      headerTooltip: "Date when pricing was received",
      field: "RECEIVED_DATE",
      valueFormatter: params => formatDate(params.value),
      valueParser: params => formatDate(params.newValue),
      cellEditor: 'agDateCellEditor',
      cellEditorParams: {
        browserDatePicker: true
      }
    },
    {
      headerName: "Email",
      headerTooltip: "Email address",
      field: "EMAIL",
      minWidth: 200
    },
    {
      headerName: "Notes",
      headerTooltip: "Additional notes or comments",
      field: "NOTES",
      minWidth: 200
    },
    {
      headerName: "Modified By",
      headerTooltip: "User who last modified this entry (system field - not editable)",
      field: "MODIFIED_BY",
      editable: false,
      cellClass: 'ag-cell-readonly'
    },
    {
      headerName: "Modified Date",
      headerTooltip: "Date when this entry was last modified (system field - not editable)",
      field: "MODIFIED_DATE",
      editable: false,
      cellClass: 'ag-cell-readonly',
      cellRenderer: DateTimeRenderer
    },
  ], [handleViewHistory, handleDuplicateEntry, handleDeleteEntry, getCellClass, canWritePricing, isLoading]);

  const defaultColDef = useMemo(() => ({
    sortable: true,
    filter: true,
    resizable: true,
    editable: !isLoading,
    cellClass: getCellClass,
    cellStyle: {
      color: theme.palette.text.primary,
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.divider
    },
    menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
    lockPinned: false
  }), [theme, isLoading, getCellClass]);

  // Grid options are now handled internally by PamGrid

  const excelStyles = useMemo(() => [
    {
      id: 'dateFormat',
      dataType: 'DateTime',
      numberFormat: {
        format: 'yyyy-mm-dd'
      }
    },
    {
      id: 'priceFormat',
      dataType: 'Number',
      numberFormat: {
        format: '#,##0.0000'
      }
    },
    {
      id: 'textFormat',
      dataType: 'String'
    }
  ], []);

  // AG Grid modules are now handled internally by PamGrid

  const handleBlankRow = useCallback(() => {
    const nextRowCount = newRowCount + 1;
    setNewRowCount(nextRowCount);


    const newRow = {};
    // Add temporary tracking field that won't change
    newRow['TEMP_ROW_ID'] = `temp_${nextRowCount}_${Date.now()}`;

    columnDefs.forEach(def => {
      const col = def.field;
      if (col === "ID") {
        newRow[col] = `new_row_${nextRowCount}`;
      } else if (col === "BROKER") {
        newRow[col] = `new_broker_${nextRowCount}`;
      } else if (col === "ID2") {
        newRow[col] = `new_id2_${nextRowCount}`;
      } else if (col === "MODIFIED_BY" || col === "MODIFIED_DATE") {
        // A new key: null MODIFIED_DATE token; the writer stamps both on save.
        newRow[col] = null;
      } else if (col === "ASOFDT") {
        // Ensure ASOFDT is set to the current selected date
        const asofdt = selectedDate || getCurrentDateString();
        newRow[col] = asofdt;
      } else if (col === "USE_PRICE") {
        // Default USE_PRICE to Y for new rows
        newRow[col] = 'Y';
      } else {
        newRow[col] = '';
      }
    });

    setRowData(prev => [newRow, ...prev]);
  }, [columnDefs, newRowCount, selectedDate, getCurrentDateString]);

  const onCellValueChanged = useCallback((params) => {

    const { data, colDef, newValue, oldValue } = params;
    const field = colDef.field;

    // AG Grid has already written newValue into data, so the key the row had
    // before this edit comes from oldValue. A loaded row's first key edit gives
    // it a KEY_EDIT_ID to be tracked by from then on: its new key may be one
    // another row already has, and the two rows' edits must stay apart.
    const isKeyEdit = PRICING_KEY_FIELDS.includes(field);
    const rowBefore = { ...data, [field]: oldValue };
    const previousKey = pricingTrackingKey(rowBefore);
    if (isKeyEdit) assignKeyEditId(data);
    const trackingKey = pricingTrackingKey(data);

    // Update row data immediately. An edited key names a different row, so it
    // no longer has a loaded MODIFIED_DATE: the save sends null for it, unless
    // the edit takes the row back to its own key.
    const keyModifiedDate = isKeyEdit && !data.TEMP_ROW_ID
      ? keyEditModifiedDate(loadedKeysRef.current, { rowBefore, rowAfter: data, keyFields: PRICING_KEY_FIELDS, previousKey, trackingKey })
      : null;
    setRowData(prev => prev.map(row => {
      if (row === data) {
        const updatedRow = {
          ...row,
          [field]: newValue,
        };
        return isKeyEdit ? { ...updatedRow, MODIFIED_DATE: keyModifiedDate } : updatedRow;
      }
      return row;
    }));

    // Track the change for highlighting and saving. A first key edit moves the
    // row's earlier edits to its KEY_EDIT_ID.
    setUpdatedRows(prev => trackCellEdit(prev, { previousKey, trackingKey, field, oldValue, newValue }));
  }, []);



  const getUpdatedRowCount = useCallback(() => {
    return Object.keys(updatedRows).length;
  }, [updatedRows]);



  // Validation function for pricing data
  const validatePricingRow = useCallback((row, isUpload = false) => {
    const errors = [];

    // Required field validations
    if (!row.BROKER || row.BROKER.trim() === '') {
      errors.push('BROKER is required');
    }
    if (!row.ID2 || row.ID2.trim() === '') {
      errors.push('ID2 is required');
    }
    if (!row.ID || row.ID.trim() === '') {
      errors.push('ID is required');
    }
    // PRICE validation - must be present and a valid number (zero is allowed)
    if (isValueNull(row.PRICE)) {
      errors.push('PRICE is required');
    } else if (typeof row.PRICE === 'number' && isNaN(row.PRICE)) {
      errors.push('PRICE must be a valid number');
    } else if (typeof row.PRICE === 'number' && row.PRICE < 0) {
      errors.push('PRICE must be greater than or equal to 0');
    } else if (typeof row.PRICE === 'string' && (row.PRICE.trim() === '' || isNaN(parseFloat(row.PRICE)))) {
      errors.push('PRICE must be a valid number');
    }
    if (!isUpload && (!row.USE_PRICE || row.USE_PRICE.trim() === '')) {
      errors.push('USE_PRICE is required');
    }

    return errors;
  }, []);


  // Save modified rows
  const handleSave = useCallback(async () => {
    // Clear any upload success messages when user clicks save
    setSuccessMessage(null);
    // Invalidate any pending clear before validation or the request can replace
    // the banner.
    clearActionError();

    // First, validate all rows and collect validation errors
    const validationErrors = [];
    const modifiedRowsData = [];

    Object.keys(updatedRows).forEach(trackingKey => {
      // Find the current row data in the grid by its tracking key
      const currentRowData = rowData.find(row => pricingTrackingKey(row) === trackingKey);

      if (!currentRowData) {
        console.warn(`Could not find row data for tracking key: ${trackingKey}`);
        return;
      }

      // Validate the row
      const rowErrors = validatePricingRow(currentRowData);
      if (rowErrors.length > 0) {
        const rowIdentifier = currentRowData.TEMP_ROW_ID ?
          `New row` :
          `Row (${currentRowData.BROKER} - ${currentRowData.ID2})`;
        validationErrors.push(`${rowIdentifier}: ${rowErrors.join(', ')}`);
        return;
      }

      // A copy of the row carrying its loaded MODIFIED_DATE unchanged (null for
      // a new row or a broker CUSIP with no pricing row yet)
      let rowToSave = withLoadedModifiedDate(currentRowData);

      // For new rows, ensure ASOFDT is set to the current selected date
      if (currentRowData.TEMP_ROW_ID) {
        const currentAsofdt = selectedDate || getCurrentDateString();
        rowToSave.ASOFDT = currentAsofdt;

        // Remove the temporary tracking field before saving to API
        delete rowToSave.TEMP_ROW_ID;
      }

      // MODIFIED_BY / MODIFIED_DATE are never restamped here: MODIFIED_DATE is
      // the concurrency token and the writer stamps both.

      // Normalize boolean field for API
      rowToSave.USE_PRICE = normalizeBooleanValue(rowToSave.USE_PRICE);

      // Remove virtual fields that shouldn't be sent to API
      delete rowToSave.actions;

      // Remove all fields that begin with underscore
      Object.keys(rowToSave).forEach(key => {
        if (key.startsWith('_')) {
          delete rowToSave[key];
        }
      });

      modifiedRowsData.push(rowToSave);
    });

    if (validationErrors.length > 0) {
      showTemporaryActionError(`Validation errors:\n${validationErrors.join('\n')}`, 10000);
      return;
    }

    // Two rows under one ID2|BROKER would be saved as two marks the grid
    // cannot tell apart again (or, with the same writer key, the later would
    // silently replace the earlier). Check every row on screen, unchanged and
    // new ones included, and save nothing until the user resolves it.
    const duplicates = findDuplicateKeys({
      rows: rowData,
      keyOf: pricingSaveKey,
      isPending: row => Boolean(updatedRows[pricingTrackingKey(row)]),
      isCandidate: isPricingRow,
    });
    if (duplicates.length > 0) {
      showActionError(duplicateKeysMessage(duplicates, {
        keyLabel: 'ID2 + Broker',
        describe: row => `${row.ID2} / ${row.BROKER}`,
      }));
      return;
    }

    const modifiedRows = modifiedRowsData.map(row => convertEmptyDatesToNull(row));

    if (modifiedRows.length === 0) {
      showTemporaryActionError('No changes to save.', 3000);
      return;
    }

    try {
      clearActionError();
      setSuccessMessage(null);
      setIsLoading(true);
      await savePricing(modifiedRows);

      // Reload the grid data to reflect any server-side changes
      await loadEntries(false, false); // preserveLocalChanges=false, silent=false

      // Ensure updatedRows is cleared and no new rows remain after reload completes
      setUpdatedRows({});

      // Remove any remaining new rows with TEMP_ROW_ID (safety check)
      setRowData(prev => prev.filter(row => !row.TEMP_ROW_ID));

      setSuccessMessage(`Successfully saved ${modifiedRows.length} modified rows.`);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      console.error('Error saving pricing data:', error);
      if (isPermissionDenied(error)) {
        showActionError(getPermissionDeniedText(error), error);
      } else {
        showActionError(getWriteErrorMessage(error, 'Failed to save changes. Please try again.'));
      }
      if (isVersionConflict(error)) {
        // Reload, keeping the unsaved edits visible. Silent, so the conflict
        // message stays on the banner. Never retry with a refreshed MODIFIED_DATE.
        await loadEntries(true, true, { adoptServerModifiedDates: true });
      }
    } finally {
      setIsLoading(false);
    }
  }, [updatedRows, rowData, convertEmptyDatesToNull, loadEntries, selectedDate, validatePricingRow, getCurrentDateString, clearActionError, showActionError, showTemporaryActionError]);

  // Filter row data to show only highlighted rows if filter is enabled
  const filteredRowData = useMemo(() => {
    if (!showOnlyHighlighted) {
      return rowData;
    }

    const filtered = rowData.filter(row => {
      // Show rows that are missing from either pricing or broker data except IDC and JPM Pricing Direct
      const isMissingFromPricing = row._isInBrokerData && !row._isInPricingData;
      const isMissingFromBroker = row._isInPricingData && !row._isInBrokerData && !["IDC", "JPM Pricing Direct"].includes(row.BROKER);

      return isMissingFromPricing || isMissingFromBroker;
    });

    return filtered;
  }, [rowData, showOnlyHighlighted]);

  // Update displayed row count when filtered data changes
  useEffect(() => {
    setDisplayedRowCount(filteredRowData.length);
  }, [filteredRowData]);

  // Update displayed row count when quick filter changes
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      // Add a small delay to allow the grid to process the filter change
      const timeoutId = setTimeout(() => {
        // The grid may be gone by the time this fires.
        const api = gridRef.current?.api;
        if (api) setDisplayedRowCount(api.getDisplayedRowCount());
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [quickFilterText, gridRef]);

  // Callback to handle grid model changes (when filters are applied)
  const onGridReady = useCallback((params) => {
    const updateDisplayedCount = () => {
      if (params.api) {
        const displayedRowCount = params.api.getDisplayedRowCount();
        setDisplayedRowCount(displayedRowCount);
      }
    };

    // Update count when grid is ready
    updateDisplayedCount();

    // Update count when model changes (filters, sorting, etc.)
    params.api.addEventListener('modelUpdated', updateDisplayedCount);
  }, []);

  // Compose the local displayed-count wiring with the config hook's grid-ready
  // bridge so neither clobbers the other.
  const handleGridReady = useCallback((params) => {
    onGridReady(params);
    gridConfigs.onGridReady(params);
  }, [onGridReady, gridConfigs]);

  const getHighlightedRowCount = useCallback(() => {
    return rowData.filter(row => {
      // Count rows that are missing from either pricing or broker data except IDC and JPM Pricing Direct
      const isMissingFromPricing = row._isInBrokerData && !row._isInPricingData;
      const isMissingFromBroker = row._isInPricingData && !row._isInBrokerData && !["IDC", "JPM Pricing Direct"].includes(row.BROKER);

      return isMissingFromPricing || isMissingFromBroker;
    }).length;
  }, [rowData]);

  const handleDownloadTemplate = useCallback(() => {
    // Create two-sheet Excel file using XLSX library
    const workbook = XLSX.utils.book_new();

    // Sheet 1: Pricing Received
    const pricingReceivedData = [
      // Header row
      ['As of Date', 'Broker', 'CUSIP', 'Description', 'Price'],
      // Sample data rows
      [selectedDate || getCurrentDateString(), 'Goldman Sachs', '9128285Q8', 'US Treasury 2.25% 11/15/2024', '99.8750'],
      [selectedDate || getCurrentDateString(), 'JPMorgan', '9128285R6', 'US Treasury 2.50% 12/15/2024', '100.1250'],
      [selectedDate || getCurrentDateString(), 'Morgan Stanley', '9128285S4', 'US Treasury 2.75% 01/15/2025', '100.3750'],
      [selectedDate || getCurrentDateString(), 'Bank of America', '9128285T2', 'US Treasury 3.00% 02/15/2025', '100.6250']
    ];

    const pricingReceivedSheet = XLSX.utils.aoa_to_sheet(pricingReceivedData);
    XLSX.utils.book_append_sheet(workbook, pricingReceivedSheet, 'Pricing Received');

    // Sheet 2: IDC & JPM Pricing (starting on row 7)
    const idcJpmData = [
      // Empty rows 1-6
      ['', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],
      ['', '', '', '', '', '', '', '', ''],
      // Header row (row 7)
      ['Identifier', 'CUSIP', 'Description', 'IDC Price', 'IDC Price Date', 'IDC Price Source', 'Broker Price', 'Broker Price Date', 'Broker Description'],
      // Sample data rows
      ['UST-001', '9128285Q8', 'US Treasury 2.25% 11/15/2024', '99.8750', '2024-01-15', 'IDC', '99.8750', '2024-01-15', 'Goldman Sachs'],
      ['UST-002', '9128285R6', 'US Treasury 2.50% 12/15/2024', '100.1250', '2024-01-15', 'IDC', '100.1250', '2024-01-15', 'JPMorgan'],
      ['UST-003', '9128285S4', 'US Treasury 2.75% 01/15/2025', '100.3750', '2024-01-15', 'IDC', '100.3750', '2024-01-15', 'Morgan Stanley'],
      ['UST-004', '9128285T2', 'US Treasury 3.00% 02/15/2025', '100.6250', '2024-01-15', 'IDC', '100.6250', '2024-01-15', 'Bank of America']
    ];

    const idcJpmSheet = XLSX.utils.aoa_to_sheet(idcJpmData);
    XLSX.utils.book_append_sheet(workbook, idcJpmSheet, 'IDC & JPM Pricing');

    // Download the file
    XLSX.writeFile(workbook, 'pricing-template.xlsx');
  }, [selectedDate, getCurrentDateString]);


  // Helper function to convert DD-Mon date format to YYYY-MM-DD
  const convertDateFromDDMon = useCallback((dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return '';

    // Handle formats like "30-Sep", "1-Jan", etc.
    const match = dateStr.match(/^(\d{1,2})-([A-Za-z]{3})$/);
    if (!match) return dateStr; // Return as-is if not in expected format

    const day = match[1].padStart(2, '0');
    const monthAbbr = match[2];

    // Map month abbreviations to numbers
    const monthMap = {
      'Jan': '01', 'Feb': '02', 'Mar': '03', 'Apr': '04',
      'May': '05', 'Jun': '06', 'Jul': '07', 'Aug': '08',
      'Sep': '09', 'Oct': '10', 'Nov': '11', 'Dec': '12'
    };

    const monthNum = monthMap[monthAbbr];
    if (!monthNum) return dateStr; // Return as-is if month not recognized

    // Use the year from the selected date (timezone agnostic)
    const selectedYear = selectedDate.split('-')[0];

    return `${selectedYear}-${monthNum}-${day}`;
  }, [selectedDate]);

  // Helper function to check if a date is within 3 calendar days of selectedDate.
  // Real calendar days (across month and year ends), timezone agnostic.
  const isWithinThreeDaysOfSelectedDate = useCallback((dateStr) => {
    if (!dateStr || !selectedDate) return false;

    const uploadedDay = calendarDayNumber(dateStr);
    const selectedDay = calendarDayNumber(selectedDate);
    if (uploadedDay === null || selectedDay === null) return false;

    return Math.abs(uploadedDay - selectedDay) <= 3;
  }, [selectedDate]);

  // Helper function to process "Pricing Received" sheet
  const processPricingReceivedSheet = useCallback((dataRows, sheetName, sheetIndex) => {
    return dataRows
      .map(row => {
        // Extract required fields
        const asOfDate = row[0] || '';
        const broker = row[1] || '';
        const cusip = row[2] || '';
        const price = row[4];

        // Check if required fields are missing
        const hasValidAsOfDate = asOfDate && asOfDate !== '';
        const hasValidBroker = broker && broker !== '';
        const hasValidCusip = cusip && cusip !== '';
        const hasValidPrice = price && price !== '' && price !== null && !isNaN(parseFloat(price));

        // Skip rows with missing required fields
        if (!hasValidAsOfDate || !hasValidBroker || !hasValidCusip || !hasValidPrice) {
          return null;
        }

        const rowObj = {
          // Map As of Date to ASOFDT and convert DD-Mon format
          ASOFDT: convertDateFromDDMon(asOfDate), // As of Date column
          // Map Broker to BROKER
          BROKER: broker, // Broker column
          // Map CUSIP to both ID and ID2
          ID: String(cusip).toUpperCase(), // CUSIP column as ID
          ID2: String(cusip).toUpperCase(), // CUSIP column as ID2
          // Map Description to DESCRIPTION
          DESCRIPTION: row[3] || '', // Description column
          // Map Price to PRICE
          PRICE: parseFloat(price), // Price column
          // Set default values for other fields
          EMAIL: '',
          NOTES: '',
          RECEIVED_DATE: '',
        };

        return rowObj;
      })
      .filter(row => row !== null); // Remove null entries (filtered out rows)
  }, [convertDateFromDDMon]);

  // Helper function to process "IDC & JPM Pricing" sheet
  const processIdcJpmPricingSheet = useCallback((dataRows, sheetName, sheetIndex) => {
    const sheetData = [];

    dataRows.forEach((row, rowIndex) => {
      // Common fields for both IDC and Broker rows
      const identifier = String(row[0] || '').toUpperCase(); // Identifier column (ID2)
      const cusip = String(row[1] || '').toUpperCase(); // CUSIP column (ID)
      const description = row[2] || ''; // Description column

      // Skip rows with empty identifier or cusip
      if (!identifier || identifier === '---' || !cusip || cusip === '---') return;

      // Get all required fields
      const idcPrice = row[3]; // IDC Price
      const idcPriceDate = row[4]; // IDC Price Date
      const idcPriceSource = row[5]; // IDC Price Source
      const brokerPrice = row[6]; // Broker Price
      const brokerPriceDate = row[7]; // Broker Price Date
      const brokerDescription = row[8]; // Broker Description

      // Check if IDC fields are valid (null, empty, or "---" means invalid). A
      // price that is not a number (e.g. "N/A") is invalid too, as on the
      // "Pricing Received" sheet; it would otherwise be saved as a 0 mark.
      const hasInvalidIdcFields = !idcPrice || idcPrice === '' || idcPrice === '---' || isNaN(parseFloat(idcPrice)) ||
        !idcPriceDate || idcPriceDate === '' || idcPriceDate === '---' ||
        !idcPriceSource || idcPriceSource === '' || idcPriceSource === '---';

      // Check if Broker fields are valid (null, empty, or "---" means invalid)
      const hasInvalidBrokerFields = !brokerPrice || brokerPrice === '' || brokerPrice === '---' || isNaN(parseFloat(brokerPrice)) ||
        !brokerPriceDate || brokerPriceDate === '' || brokerPriceDate === '---' ||
        !brokerDescription || brokerDescription === '' || brokerDescription === '---';

      // Create IDC row if IDC data exists and IDC fields are valid
      if (!hasInvalidIdcFields) {
        // For IDC dates, if within 3 days of selectedDate, use selectedDate instead
        const finalIdcDate = isWithinThreeDaysOfSelectedDate(idcPriceDate) ? selectedDate : idcPriceDate;

        const idcRow = {
          ASOFDT: finalIdcDate,
          BROKER: idcPriceSource,
          ID: cusip,
          ID2: identifier,
          PRICE: parseFloat(idcPrice) || 0,
          DESCRIPTION: description,
          EMAIL: '',
          NOTES: '',
          RECEIVED_DATE: '',
        };
        sheetData.push(idcRow);
      }

      // Create Broker row if Broker data exists and Broker fields are valid
      if (!hasInvalidBrokerFields) {
        const brokerRow = {
          ASOFDT: brokerPriceDate,
          BROKER: brokerDescription,
          ID: cusip,
          ID2: identifier,
          PRICE: parseFloat(brokerPrice) || 0,
          DESCRIPTION: description,
          EMAIL: '',
          NOTES: '',
          RECEIVED_DATE: '',
        };
        sheetData.push(brokerRow);
      }
    });

    return sheetData;
  }, [selectedDate, isWithinThreeDaysOfSelectedDate]);

  // Upload dropdown menu handlers
  const handleUploadMenuClick = useCallback((event) => {
    setUploadMenuAnchor(event.currentTarget);
  }, []);

  const handleUploadMenuClose = useCallback(() => {
    setUploadMenuAnchor(null);
  }, []);

  const handleUploadPricingReceived = useCallback(() => {
    setUploadType('pricing-received');
    handleUploadMenuClose();
    fileInputRef.current?.click();
  }, [handleUploadMenuClose]);

  const handleUploadIdcJpm = useCallback(() => {
    setUploadType('idc-jpm');
    handleUploadMenuClose();
    fileInputRef.current?.click();
  }, [handleUploadMenuClose]);

  const handleUploadBoth = useCallback(() => {
    setUploadType('both');
    handleUploadMenuClose();
    fileInputRef.current?.click();
  }, [handleUploadMenuClose]);

  // Validation function for "Pricing Received" sheet structure
  const validatePricingReceivedSheet = useCallback((rawData) => {
    if (!rawData || rawData.length === 0) {
      throw new Error('"Pricing Received" sheet is empty');
    }

    const headers = rawData[0];
    const expectedHeaders = ['As of Date', 'Broker', 'CUSIP', 'Description', 'Price'];

    // Check if all required headers are present
    const missingHeaders = expectedHeaders.filter(expected =>
      !headers.some(header => header && header.toString().toLowerCase().includes(expected.toLowerCase()))
    );

    if (missingHeaders.length > 0) {
      throw new Error(`"Pricing Received" sheet is missing required columns: ${missingHeaders.join(', ')}. Expected columns: ${expectedHeaders.join(', ')}`);
    }

    // Check if there's any data beyond headers
    if (rawData.length < 2) {
      throw new Error('"Pricing Received" sheet contains only headers but no data rows');
    }

    return true;
  }, []);

  // Validation function for "IDC & JPM Pricing" sheet structure
  const validateIdcJpmPricingSheet = useCallback((rawData) => {
    if (!rawData || rawData.length === 0) {
      throw new Error('"IDC & JPM Pricing" sheet is empty');
    }

    const headers = rawData[6];
    const expectedHeaders = ['Identifier', 'CUSIP', 'Description', 'IDC Price', 'IDC Price Date', 'IDC Price Source', 'Broker Price', 'Broker Price Date', 'Broker Description'];

    // Check if all required headers are present
    const missingHeaders = expectedHeaders.filter(expected =>
      !headers.some(header => header && header.toString().toLowerCase().includes(expected.toLowerCase()))
    );

    if (missingHeaders.length > 0) {
      throw new Error(`"IDC & JPM Pricing" sheet is missing required columns: ${missingHeaders.join(', ')}. Expected columns: ${expectedHeaders.join(', ')}`);
    }

    // Check if there's any data beyond headers
    if (rawData.length < 2) {
      throw new Error('"IDC & JPM Pricing" sheet contains only headers but no data rows');
    }

    return true;
  }, []);

  // Validation function to check required sheets are present
  const validateRequiredSheets = useCallback((workbook, uploadType) => {
    const sheetNames = workbook.SheetNames;
    const availableSheets = sheetNames.join(', ');

    if (uploadType === 'pricing-received') {
      if (!workbook.Sheets['Pricing Received']) {
        throw new Error(`Excel file must contain "Pricing Received" sheet. Available sheets: ${availableSheets}`);
      }
    } else if (uploadType === 'idc-jpm') {
      if (!workbook.Sheets['IDC & JPM Pricing']) {
        throw new Error(`Excel file must contain "IDC & JPM Pricing" sheet. Available sheets: ${availableSheets}`);
      }
    } else if (uploadType === 'both') {
      const missingSheets = [];
      if (!workbook.Sheets['Pricing Received']) {
        missingSheets.push('"Pricing Received"');
      }
      if (!workbook.Sheets['IDC & JPM Pricing']) {
        missingSheets.push('"IDC & JPM Pricing"');
      }

      if (missingSheets.length > 0) {
        throw new Error(`Excel file must contain both required sheets. Missing: ${missingSheets.join(', ')}. Available sheets: ${availableSheets}`);
      }
    }

    return true;
  }, []);

  // Process Excel data based on upload type
  const processExcelSheets = useCallback((arrayBuffer) => {
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetNames = workbook.SheetNames;

    // Validate required sheets are present first
    validateRequiredSheets(workbook, uploadType);

    const allData = [];

    // Process sheets based on upload type
    if (uploadType === 'pricing-received') {
      // Only process "Pricing Received" sheet
      const pricingSheet = workbook.Sheets['Pricing Received'];

      const rawData = XLSX.utils.sheet_to_json(pricingSheet, {
        header: 1,
        defval: '',
        raw: false
      });

      // Validate sheet structure
      validatePricingReceivedSheet(rawData);

      if (rawData.length > 0) {
        const dataRows = rawData.slice(1);
        const sheetData = processPricingReceivedSheet(dataRows, 'Pricing Received', 0);
        allData.push(...sheetData);
      }
    } else if (uploadType === 'idc-jpm') {
      // Only process "IDC & JPM Pricing" sheet
      const idcJpmSheet = workbook.Sheets['IDC & JPM Pricing'];

      const rawData = XLSX.utils.sheet_to_json(idcJpmSheet, {
        header: 1,
        defval: '',
        raw: false
      });

      // Validate sheet structure
      validateIdcJpmPricingSheet(rawData);

      if (rawData.length > 0) {
        const dataRows = rawData.slice(1);
        const sheetData = processIdcJpmPricingSheet(dataRows, 'IDC & JPM Pricing', 0);
        allData.push(...sheetData);
      }
    } else if (uploadType === 'both') {
      // Process both sheets (original behavior)
      if (sheetNames.length < 2) {
        throw new Error('Excel file must contain at least 2 sheets');
      }

      // Process each sheet
      sheetNames.forEach((sheetName, index) => {
        const worksheet = workbook.Sheets[sheetName];
        const rawData = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: '',
          raw: false
        });

        if (rawData.length === 0) return;

        // Validate sheet structure based on sheet name
        if (sheetName === 'Pricing Received') {
          validatePricingReceivedSheet(rawData);
        } else if (sheetName === 'IDC & JPM Pricing') {
          validateIdcJpmPricingSheet(rawData);
        }

        const dataRows = rawData.slice(1);

        let sheetData = [];

        // Route to appropriate sheet processor
        if (sheetName === 'Pricing Received') {
          sheetData = processPricingReceivedSheet(dataRows, sheetName, index);
        } else if (sheetName === 'IDC & JPM Pricing') {
          sheetData = processIdcJpmPricingSheet(dataRows, sheetName, index);
        }

        allData.push(...sheetData);
      });
    }

    return allData;
  }, [uploadType, processPricingReceivedSheet, processIdcJpmPricingSheet, validatePricingReceivedSheet, validateIdcJpmPricingSheet, validateRequiredSheets]);

  // File upload handler
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      clearActionError();
      setSuccessMessage(null);
      setIsLoading(true);

      const fileExtension = file.name.toLowerCase().split('.').pop();

      if (!['xlsx', 'xls'].includes(fileExtension)) {
        throw new Error('Please upload an Excel file (.xlsx or .xls)');
      }

      // Read file as array buffer
      const arrayBuffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });

      // Process both sheets
      const processedData = processExcelSheets(arrayBuffer);

      if (processedData.length === 0) {
        throw new Error('No data found in the Excel file');
      }

      // Process the data for the grid
      const allProcessedRows = processedData.map((row, index) => {
        const newRow = {
          TEMP_ROW_ID: `upload_${Date.now()}_${index}`,
          BROKER: row.BROKER || '',
          ID: row.ID || '',
          ID2: row.ID2 || '',
          PRICE: parseFloat(row.PRICE) || 0,
          DESCRIPTION: row.DESCRIPTION || '',
          EMAIL: row.EMAIL || '',
          NOTES: row.NOTES || '',
          // As loaded rows hold it (YYYY-MM-DD), so 8/31/26 matches 2026-08-31
          // below instead of adding a second row for it. A date that does not
          // parse is kept as given, and the date filter drops it.
          ASOFDT: row.ASOFDT ? (toCalendarDate(row.ASOFDT) || row.ASOFDT) : selectedDate,
          RECEIVED_DATE: row.RECEIVED_DATE || '',
          USE_PRICE: '',
          // Never from the file or the clock: a matched row keeps its loaded
          // MODIFIED_DATE (see the merge below), a new key sends null.
          MODIFIED_BY: null,
          MODIFIED_DATE: null,
        };

        return newRow;
      });

      // Validate dropdown field values against allowed options
      const validateUsePrice = createChoicesValidator('USE_PRICE', config.booleanOptions);
      const dropdownErrors = [];
      allProcessedRows.forEach((row, idx) => {
        const err = validateUsePrice(row);
        if (err) {
          dropdownErrors.push(`Row ${idx + 1}: ${err.field} value "${err.value}" is not valid. ${err.message}`);
        }
      });

      if (dropdownErrors.length > 0) {
        throw new Error(`Upload validation failed:\n${dropdownErrors.join('\n')}`);
      }

      // Filter rows to only include those with dates matching selectedDate
      const selectedCalendarDate = toCalendarDate(selectedDate);
      const newRows = allProcessedRows.filter(row => {
        const rowDate = toCalendarDate(row.ASOFDT);
        return rowDate !== '' && rowDate === selectedCalendarDate;
      });

      // Merge uploaded rows with the grid as it is now (after the file read),
      // here and synchronously: counting inside a setRowData updater left the
      // counts at 0 when the banner below was built, since React may run the
      // updater later. The state updates that follow are pure.
      let mergedRowsCount = 0;
      let newRowsCount = 0;
      const updatedGrid = [...rowDataRef.current];
      const uploadTrackingEntries = {};

      newRows.forEach(uploadedRow => {
        // Find ALL existing rows by matching date, broker, and ID
        const existingRowIndices = updatedGrid
          .map((existingRow, index) => ({ row: existingRow, index }))
          .filter(({ row }) =>
            toCalendarDate(row.ASOFDT) === uploadedRow.ASOFDT &&
            row.BROKER === uploadedRow.BROKER &&
            row.ID === uploadedRow.ID
          );

        if (existingRowIndices.length > 0) {
          // Merge with all matching existing rows
          mergedRowsCount += existingRowIndices.length;
          existingRowIndices.forEach(({ row: existingRow, index: existingRowIndex }) => {
            const mergedRow = { ...existingRow };

            // Apply merge rules for each field
            Object.keys(uploadedRow).forEach(field => {
              if (field.startsWith('_') || field === 'TEMP_ROW_ID') return;
              // The matched row's loaded MODIFIED_DATE is its token; the writer stamps MODIFIED_BY.
              if (field === 'MODIFIED_DATE' || field === 'MODIFIED_BY') return;
              // The match is on ASOFDT/BROKER/ID; the row keeps its own key. An
              // uploaded ID2 (the bare CUSIP) would rename a "-U" row and detach
              // its tracked edits.
              if (PRICING_KEY_FIELDS.includes(field)) return;

              const uploadedValue = uploadedRow[field];
              const existingValue = existingRow[field];

              // Special handling for USE_PRICE
              if (field === 'USE_PRICE') {
                // Use existing value if it exists, use use_price map if it exists, otherwise default to 'Y'
                if (existingValue && existingValue !== '' && existingValue !== null) {
                  mergedRow[field] = existingValue;
                } else if (uploadedRow.BROKER in usePriceMap && uploadedRow.ID in usePriceMap[uploadedRow.BROKER]) {
                  const mapValue = usePriceMap[uploadedRow.BROKER][uploadedRow.ID];
                  mergedRow[field] = mapValue === true ? 'Y' : mapValue === false ? 'N' : mapValue;
                } else {
                  mergedRow[field] = 'Y';
                }
              } else if (field === 'PRICE') {
                // Special handling for PRICE field
                if (existingRow.ID2 && existingRow.ID2.endsWith('-U')) {
                  // For rows with ID2 ending in -U, set price to 0
                  mergedRow[field] = 0;
                } else {
                  // Uploaded over existing. Only a blank price is empty: 0 is a
                  // real mark (e.g. a write-down).
                  if (!isValueNull(uploadedValue)) {
                    mergedRow[field] = uploadedValue;
                  } else if (!isValueNull(existingValue)) {
                    mergedRow[field] = existingValue;
                  } else {
                    mergedRow[field] = '';
                  }
                }
              } else {
                // Apply normal precedence rules for other fields
                if (uploadedValue && uploadedValue !== '' && uploadedValue !== null) {
                  mergedRow[field] = uploadedValue;
                } else if (existingValue && existingValue !== '' && existingValue !== null) {
                  mergedRow[field] = existingValue;
                } else {
                  mergedRow[field] = uploadedValue || existingValue || '';
                }
              }
            });

            // Update the existing row
            updatedGrid[existingRowIndex] = mergedRow;

            // Track changes for existing row under its tracking key
            const existingTrackingKey = pricingTrackingKey(existingRow);
            uploadTrackingEntries[existingTrackingKey] = {
              ...uploadTrackingEntries[existingTrackingKey],
              ...Object.keys(mergedRow).reduce((acc, field) => {
                if (field !== 'TEMP_ROW_ID' && !field.startsWith('_')) {
                  // Blank (null/undefined/'') compares as '', but 0 stays 0.
                  const oldValue = isValueNull(existingRow[field]) ? '' : existingRow[field];
                  const newValue = isValueNull(mergedRow[field]) ? '' : mergedRow[field];
                  if (oldValue !== newValue) {
                    acc[field] = { oldValue, newValue };
                  }
                }
                return acc;
              }, {})
            };
          });
        } else {
          // Add as new row
          newRowsCount++;

          // Handle USE_PRICE with use price map for new rows
          if (!uploadedRow.USE_PRICE || uploadedRow.USE_PRICE === '' || uploadedRow.USE_PRICE === null) {
            const brokerMap = usePriceMap[uploadedRow.BROKER];
            if (brokerMap && uploadedRow.ID in brokerMap) {
              const mapValue = brokerMap[uploadedRow.ID];
              uploadedRow.USE_PRICE = mapValue === true ? 'Y' : mapValue === false ? 'N' : mapValue;
            } else {
              uploadedRow.USE_PRICE = 'Y'; // Default to 'Y'
            }
          }

          updatedGrid.unshift(uploadedRow);

          // Track as new row
          uploadTrackingEntries[uploadedRow.TEMP_ROW_ID] = {
            ...Object.keys(uploadedRow).reduce((acc, field) => {
              if (field !== 'TEMP_ROW_ID' && !field.startsWith('_')) {
                acc[field] = { oldValue: '', newValue: uploadedRow[field] };
              }
              return acc;
            }, {})
          };
        }
      });

      setRowData(updatedGrid);
      setUpdatedRows(prev => addTrackedChanges(prev, uploadTrackingEntries));

      const totalProcessed = allProcessedRows.length;
      const filteredCount = newRows.length;
      const message = `Successfully uploaded ${filteredCount} entries from ${file.name}. ${mergedRowsCount} rows were merged with existing data, ${newRowsCount} rows were added as new entries. ${totalProcessed - filteredCount} entries were filtered out due to date mismatch with selected date. Review the data and click Save to persist changes.`;
      setSuccessMessage(message);

    } catch (error) {
      console.error('Upload error:', error);
      showTemporaryActionError(
        error.message || 'Failed to process uploaded file. Please check the format and try again.',
        8000
      );
    } finally {
      setIsLoading(false);
      // Clear the file input and reset upload type
      event.target.value = '';
      setUploadType(null);
    }
  }, [processExcelSheets, selectedDate, usePriceMap, clearActionError, showTemporaryActionError]);

  // CW Export functionality - exports as XLSX
  const handleCWExport = useCallback(async () => {
    if (!selectedDate) return;

    try {
      clearActionError();
      setIsLoading(true);

      // Fetch pricing frequency data
      const frequencyResponse = await getPricingFrequency();
      const frequencyData = frequencyResponse.data || [];

      // Create frequency map for quick lookup
      const frequencyMap = {};
      frequencyData.forEach(item => {
        if (item.ID2 && item.FREQUENCY) {
          frequencyMap[item.ID2] = item.FREQUENCY;
        }
      });

      // Helper function to calculate end date based on frequency
      // Uses timezone-agnostic date string manipulation to avoid timezone issues
      const calculateEndDate = (id2) => {
        const frequency = frequencyMap[id2] || 'Monthly'; // Default to Monthly if not found
        const monthsToAdd = frequency === 'Quarterly' ? 3 : 1;

        // Parse the selectedDate as YYYY-MM-DD string (timezone agnostic)
        const [beginYear, beginMonth, beginDay] = selectedDate.split('-').map(Number);

        // Calculate target year and month
        let targetYear = beginYear;
        let targetMonth = beginMonth + monthsToAdd;

        // Handle year rollover
        while (targetMonth > 12) {
          targetMonth -= 12;
          targetYear += 1;
        }

        // Handle month-end dates (e.g., Aug 31 -> Nov 30, Apr 30 -> Jul 31, Jan 31 -> Feb 28)
        // Check if the begin date is the last day of the month
        const lastDayOfBeginMonth = new Date(beginYear, beginMonth, 0).getDate();
        let targetDay = beginDay;

        if (beginDay === lastDayOfBeginMonth) {
          // If begin date is last day of month, end date should be last day of target month
          const lastDayOfTargetMonth = new Date(targetYear, targetMonth, 0).getDate();
          targetDay = lastDayOfTargetMonth;
        } else {
          // For regular dates, check if the target day exists in the target month
          const lastDayOfTargetMonth = new Date(targetYear, targetMonth, 0).getDate();
          if (beginDay > lastDayOfTargetMonth) {
            // If the day doesn't exist in target month, use the last day of that month
            targetDay = lastDayOfTargetMonth;
          }
        }

        // Format as YYYY-MM-DD string (timezone agnostic)
        const targetMonthStr = targetMonth.toString().padStart(2, '0');
        const targetDayStr = targetDay.toString().padStart(2, '0');

        return `${targetYear}-${targetMonthStr}-${targetDayStr}`;
      };

      // Filter and process data (same logic as before)
      let filteredData = rowData.filter(entry =>
        entry.USE_PRICE === 'Y' || entry.USE_PRICE === true || entry.USE_PRICE === 'true'
      );

      if (filteredData.length === 0) {
        filteredData = rowData;
      }

      const groupedData = {};
      filteredData.forEach(entry => {
        // A finite PRICE of 0 is a valid mark (validation allows it) and is
        // exported. An empty/non-numeric PRICE is not a mark, and neither is the
        // default 0 of a placeholder row the grid adds for a broker CUSIP that
        // has no pricing entry yet.
        const price = entry.PRICE === '' || entry.PRICE == null ? NaN : parseFloat(entry.PRICE);
        const isUnquotedPlaceholder = entry._isInPricingData === false && price === 0;
        if (entry.ID2 && !isUnquotedPlaceholder) {
          const id2 = entry.ID2;

          if (Number.isFinite(price)) {
            if (!groupedData[id2]) {
              groupedData[id2] = {
                prices: [],
                brokers: []
              };
            }
            groupedData[id2].prices.push(price);
            groupedData[id2].brokers.push(entry.BROKER);
          }
        }
      });

      const cwExportData = Object.keys(groupedData).map(id2 => {
        const group = groupedData[id2];
        const meanPrice = group.prices.reduce((sum, price) => sum + price, 0) / group.prices.length;
        const uniqueBrokers = [...new Set(group.brokers)];
        const overrideSource = calculateSourceType(id2, uniqueBrokers.length, uniqueBrokers[0]);

        // For grouped data, use the first broker or determine NAIC source based on broker mix
        const primaryBroker = uniqueBrokers[0];
        const naicSource = calculateNAICSource(primaryBroker);

        // Calculate end date based on frequency
        const beginDate = selectedDate;
        const endDate = calculateEndDate(id2, beginDate);

        return [
          '43960', // PAM Client ID
          String(id2), // CUSIP - will be formatted as text via Excel styles
          beginDate ? formatDate(beginDate) : '', // Begin Date - as string
          endDate, // End Date - as string
          overrideSource, // Notes - doubles override source for some reason
          overrideSource, // Override Source
          parseFloat(meanPrice.toFixed(4)), // Price - as number
          naicSource // NAIC Source
        ];
      });

      if (cwExportData.length === 0) {
        showTemporaryActionError('No data available for CW export.', 5000);
        return;
      }

      // Create Excel workbook
      const headers = ['Client ID', 'CUSIP', 'Begin Date', 'End Date', 'Notes', 'Override Source', 'Price', 'NAIC Source'];
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...cwExportData]);

      // Set column widths
      const columnWidths = [
        { wch: 12 }, // Client ID
        { wch: 15 }, // CUSIP
        { wch: 12 }, // Begin Date
        { wch: 12 }, // End Date
        { wch: 20 }, // Notes
        { wch: 18 }, // Override Source
        { wch: 12 }, // Price
        { wch: 12 }  // NAIC Source
      ];
      worksheet['!cols'] = columnWidths;

      // Apply Excel styles to format data properly
      const range = XLSX.utils.decode_range(worksheet['!ref']);

      // Style each row (skip header row)
      for (let row = 1; row <= range.e.r; row++) {
        // CUSIP column (B) - format as text to preserve leading zeros
        const cusipCell = XLSX.utils.encode_cell({ r: row, c: 1 });
        if (worksheet[cusipCell]) {
          worksheet[cusipCell].t = 's'; // String type
        }

        // Begin Date column (C) - keep as string to avoid timezone issues
        const beginDateCell = XLSX.utils.encode_cell({ r: row, c: 2 });
        if (worksheet[beginDateCell] && worksheet[beginDateCell].v) {
          // Convert YYYY-MM-DD to MM/DD/YYYY format for display
          const dateStr = worksheet[beginDateCell].v;
          if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const [year, month, day] = dateStr.split('-');
            worksheet[beginDateCell].v = `${month}/${day}/${year}`;
            worksheet[beginDateCell].t = 's'; // String type to avoid any date conversion
          }
        }

        // End Date column (D) - keep as string to avoid timezone issues
        const endDateCell = XLSX.utils.encode_cell({ r: row, c: 3 });
        if (worksheet[endDateCell] && worksheet[endDateCell].v) {
          // Convert YYYY-MM-DD to MM/DD/YYYY format for display
          const dateStr = worksheet[endDateCell].v;
          if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
            const [year, month, day] = dateStr.split('-');
            worksheet[endDateCell].v = `${month}/${day}/${year}`;
            worksheet[endDateCell].t = 's'; // String type to avoid any date conversion
          }
        }

        // Price column (G) - format as number with 4 decimal places
        const priceCell = XLSX.utils.encode_cell({ r: row, c: 6 });
        if (worksheet[priceCell] && worksheet[priceCell].v) {
          worksheet[priceCell].t = 'n'; // Number type
          worksheet[priceCell].z = '0.0000'; // 4 decimal places
        }
      }

      // Create workbook and add worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'CW Export');

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Download as Excel
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `cw-export-${formatDate(selectedDate).replace(/\//g, '-')}.xlsx`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setSuccessMessage(`Successfully exported ${cwExportData.length} securities in CW format.`);
      setTimeout(() => setSuccessMessage(null), 5000);

    } catch (error) {
      console.error('CW Export error:', error);
      showTemporaryActionError('Failed to generate CW export. Please try again.', 5000);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDate, rowData, calculateSourceType, calculateNAICSource, clearActionError, showTemporaryActionError]);

  if (!selectedDate) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography variant="h6" color="text.secondary">
          Please select a date to view pricing entries
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Hidden file input for Excel upload functionality */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleFileUpload}
      />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 2,
        }}
      >
        <Box>
          <Typography variant="h5">
            Pricing Entries - {formatDate(selectedDate)}
            {showOnlyHighlighted && ` (${filteredRowData.length} highlighted entries)`}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Showing {displayedRowCount} of {rowData.length} rows
            {showOnlyHighlighted && " (filtered)"}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
          {/* Per-user saved column layouts (view-config) */}
          <GridConfigMenu key="grid-config-menu" controller={gridConfigs} />
          <Tooltip title="Upload Excel file with different sheet options">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightblue",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(173, 216, 230, 0.8)'
                  }
                }}
                startIcon={<UploadIcon />}
                onClick={handleUploadMenuClick}
                disabled={isLoading}
              >
                Upload Excel
              </Button>
            </span>
          </Tooltip>
          <Menu
            anchorEl={uploadMenuAnchor}
            open={Boolean(uploadMenuAnchor)}
            onClose={handleUploadMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <MenuItem onClick={handleUploadPricingReceived}>
              Upload Pricing Received
            </MenuItem>
            <MenuItem onClick={handleUploadIdcJpm}>
              Upload IDC & JPM
            </MenuItem>
            <MenuItem onClick={handleUploadBoth}>
              Upload Both Sheets
            </MenuItem>
          </Menu>
          <Tooltip title="Download Excel template file with sample data">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightcyan",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(224, 255, 255, 0.8)'
                  }
                }}
                onClick={handleDownloadTemplate}
                startIcon={<DownloadIcon />}
                disabled={isLoading}
              >
                Template
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Export aggregated pricing data in CW format as XLSX">
            <span>
              <Button
                sx={{
                  backgroundColor: "lightsalmon",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(255, 160, 122, 0.8)'
                  }
                }}
                onClick={handleCWExport}
                startIcon={<DownloadIcon />}
                disabled={isLoading}
              >
                CW Export
              </Button>
            </span>
          </Tooltip>
          {/* 
            Add Blank Row button hidden
            renable if we want to add blank rows
            will require considering roll forward logic for use price map
          */}
          {false && (
            <Tooltip title="Add a new blank row to create a new pricing entry">
              <Button
                sx={{
                  backgroundColor: "lightgrey",
                  color: "black",
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)'
                  }
                }}
                onClick={handleBlankRow}
                disabled={isLoading}
              >
                Add Blank Row
              </Button>
            </Tooltip>
          )}
          <Tooltip title={`${showOnlyHighlighted ? 'Show all entries' : `Show only highlighted entries (${getHighlightedRowCount()} invalid broker matches)`}`}>
            <span>
              <Button
                sx={{
                  backgroundColor: showOnlyHighlighted ? "lightcoral" : "lightsteelblue",
                  color: "black",
                  '&:hover': {
                    backgroundColor: showOnlyHighlighted ? 'rgba(240, 128, 128, 0.8)' : 'rgba(176, 196, 222, 0.8)'
                  }
                }}
                startIcon={<FilterIcon />}
                onClick={() => setShowOnlyHighlighted(!showOnlyHighlighted)}
                disabled={isLoading}
              >
                {showOnlyHighlighted ? 'Show All' : `Filter (${getHighlightedRowCount()})`}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={!canWritePricing ? NO_PRICING_WRITE_MESSAGE : `Save ${getUpdatedRowCount() > 0 ? `${getUpdatedRowCount()} changed` : 'all'} pricing entries`}>
            <span>
              <Button
                sx={{
                  backgroundColor: getUpdatedRowCount() > 0 ? "orange" : "lightgrey",
                  color: "black",
                  '&:hover': {
                    backgroundColor: getUpdatedRowCount() > 0 ? 'rgba(255, 165, 0, 0.8)' : 'rgba(0, 0, 0, 0.1)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)'
                  }
                }}
                onClick={handleSave}
                disabled={!canWritePricing || isLoading || getUpdatedRowCount() === 0}
              >
                {isLoading ? 'Saving...' : `Save${getUpdatedRowCount() > 0 ? ` (${getUpdatedRowCount()})` : ''}`}
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>
      {refreshFailed && (
        <Typography color="warning.main" variant="body2" sx={{ mb: 1 }}>
          Live refresh failed — the data below may be out of date.
        </Typography>
      )}
      {loadError && (
        <Typography color="error" sx={{ mb: 2 }}>
          {loadError}
        </Typography>
      )}
      {error && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography color="error">
            {error}
          </Typography>
          {deniedError && <RequestAccessButton error={deniedError} />}
        </Box>
      )}
      {successMessage && (
        <Typography color="success.main" sx={{ mb: 2 }}>
          {successMessage}
        </Typography>
      )}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
          <CircularProgress size={24} sx={{ mr: 1 }} />
          <Typography variant="body2">Loading pricing entries...</Typography>
        </Box>
      )}
      <Box sx={{ mb: 2 }}>
        <QuickFilter
          value={quickFilterText}
          onChange={setQuickFilterText}
          placeholder="Search pricing entries..."
        />
      </Box>
      <style>{`
        .ag-cell-range-selected {
          background-color: rgba(120, 160, 240, 0.3) !important;
          border: 2px solid #3f7cac !important;
        }
        .ag-cell-range-selected-1 {
          background-color: rgba(120, 160, 240, 0.3) !important;
        }
        .ag-cell-range-selected-2 {
          background-color: rgba(120, 160, 240, 0.2) !important;
        }
        .ag-cell-range-selected-3 {
          background-color: rgba(120, 160, 240, 0.1) !important;
        }
        .ag-cell-range-selected-4 {
          background-color: rgba(120, 160, 240, 0.1) !important;
        }
        .ag-cell-focus {
          border: 2px solid #3f7cac !important;
          background-color: rgba(120, 160, 240, 0.4) !important;
        }
        .ag-cell-updated {
          background-color: rgba(255, 243, 205, 0.3) !important;
          border: 1px solid #f5d5a5 !important;
          font-weight: 500;
        }
        .ag-cell-updated-new-row {
          background-color: rgba(173, 216, 230, 0.2) !important;
          border: 1px solid #add8e6 !important;
          font-weight: 500;
        }
        .ag-cell-invalid-broker {
          background-color: rgba(255, 182, 193, 0.15) !important;
        }
        .ag-cell-missing-from-pricing {
          background-color: rgba(221, 160, 221, 0.2) !important;
        }
        .ag-cell-missing-from-broker {
          background-color: rgba(248, 215, 218, 0.3) !important;
        }
        .ag-cell-readonly {
          background-color: rgba(240, 240, 240, 0.5) !important;
          color: #666 !important;
          font-style: italic;
          cursor: not-allowed;
        }
      `}</style>
      <PamGrid
        ref={gridRef}
        rowData={filteredRowData}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        quickFilterText={quickFilterText}
        height="calc(80vh - 300px)"
        isLoading={isLoading}
        loadingMessage="Loading pricing entries..."

        // Row identification
        getRowId={getPricingRowId}

        // Cell editing
        onCellValueChanged={onCellValueChanged}
        onGridReady={handleGridReady}

        // Grid layout persistence: mirror every column/sort/filter gesture into
        // the local stash so unsaved tweaks survive a page refresh. PamGrid
        // surfaces onFilterChanged at the top level; the column events ride
        // through agGridProps as the stashBindings bundle.
        onFilterChanged={gridConfigs.onGridStateChanged}
        agGridProps={gridConfigs.stashBindings}

        // Excel styles for CW Export
        excelStyles={excelStyles}

        // Grid options
        rowHeight={32}
        enableCellTextSelection={true}
        enableRangeSelection={true}
        ensureDomOrder={true}
        undoRedoCellEditing={true}
        suppressHorizontalScroll={false}

        // Footer with copy/paste instructions
        showFooter={true}
      />

      {/* Color Legend */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 1, border: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          Color Legend
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: 'rgba(221, 160, 221, 0.2)',
                border: '1px solid #dda0dd',
                borderRadius: 1
              }}
            />
            <Typography variant="body2">
              <strong>Missing from Pricing:</strong> Row exists in broker data but not in pricing data
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: 'rgba(248, 215, 218, 0.6)',
                border: '1px solid #d4a574',
                borderRadius: 1
              }}
            />
            <Typography variant="body2">
              <strong>Missing from Broker:</strong> Row exists in pricing data but not in broker data
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: 'rgba(255, 243, 205, 0.3)',
                border: '1px solid #f5d5a5',
                borderRadius: 1
              }}
            />
            <Typography variant="body2">
              <strong>Modified:</strong> Row has unsaved changes
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                backgroundColor: 'rgba(173, 216, 230, 0.2)',
                border: '1px solid #add8e6',
                borderRadius: 1
              }}
            />
            <Typography variant="body2">
              <strong>New Row:</strong> Newly added row (not yet saved)
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.grey[100], borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Copy/Paste Instructions:</strong> Select cells or ranges and use Ctrl+C to copy, Ctrl+V to paste.
          Works with Excel and other spreadsheet applications.
        </Typography>
      </Box>

      <HistoryModal
        open={historyModalOpen}
        onClose={() => setHistoryModalOpen(false)}
        title={`Pricing Entry History - ${selectedEntryForHistory?.DESCRIPTION || selectedEntryForHistory?.BROKER || 'Entry'}`}
        height="60vh"
      >
        <PricingHistory
          asofdt={selectedEntryForHistory?.ASOFDT || selectedDate}
          id2={selectedEntryForHistory?.ID2}
          showTitle={false}
        />
      </HistoryModal>

      <ConfirmationDialog
        open={confirmationModalOpen}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Entry"
        message={`Are you sure you want to delete this pricing entry for "${entryToDelete?.DESCRIPTION || entryToDelete?.ID2}" by "${entryToDelete?.BROKER}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="error"
      />
    </Box>
  );
};

export default PricingGrid;
